<!-- eslint-disable max-lines -->
<template>
  <div class="row">
    <div class="small-12 columns">
      <div class="editor-menu">
        <div class="editor-menu__group">
          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: isHeading }"
            @click="changeHeading"
          >
            <i class="material-icons editor-menu__icon">
              title
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: isBold }"
            @click="changeStyle('bold')"
          >
            <i class="material-icons editor-menu__icon">
              format_bold
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: isItalic }"
            @click="changeStyle('italic')"
          >
            <i class="material-icons editor-menu__icon">
              format_italic
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: isUnderline }"
            @click="changeStyle('underline')"
          >
            <i class="material-icons editor-menu__icon">
              format_underline
            </i>
          </a>
        </div>

        <div v-if="hasAlignment" class="editor-menu__group">
          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: alignment === toStart }"
            @click="setTextAlignment(toStart)"
          >
            <i class="material-icons editor-menu__icon">
              format_align_{{ toStart }}
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: alignment === 'center' }"
            @click="setTextAlignment('center')"
          >
            <i class="material-icons editor-menu__icon">
              format_align_center
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: alignment === toEnd }"
            @click="setTextAlignment(toEnd)"
          >
            <i class="material-icons editor-menu__icon">
              format_align_{{ toEnd }}
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            :class="{ active: alignment === 'justify' }"
            @click="setTextAlignment('justify')"
          >
            <i class="material-icons editor-menu__icon">
              format_align_justify
            </i>
          </a>
        </div>

        <div class="editor-menu__group">
          <a
            role="button"
            class="editor-menu__item "
            @click="changeListLevel(toStart)"
          >
            <i class="material-icons editor-menu__icon">
              keyboard_arrow_{{ toStart }}
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            @click="formatListBulleted"
          >
            <i class="material-icons editor-menu__icon">
              format_list_bulleted
            </i>
          </a>

          <a
            v-if="hasListNumbered"
            role="button"
            class="editor-menu__item"
            @click="formatListNumbered"
          >
            <i class="material-icons editor-menu__icon">
              format_list_numbered
            </i>
          </a>

          <a
            role="button"
            class="editor-menu__item"
            @click="changeListLevel(toEnd)"
          >
            <i class="material-icons editor-menu__icon">
              keyboard_arrow_{{ toEnd }}
            </i>
          </a>
        </div>
      </div>
      <div
        ref="editor"
        class="editor-area"
        :dir="dir"
        :tabindex="$attrs['editor-tab-index']"
      />
    </div>
  </div>
</template>

<script>
import createDOMPurify from 'dompurify';
import Squire from '@/utils/squire';

const DOMPurify = createDOMPurify(window);

export default {
  name: 'Editor',

  props: {
    value: {
      type: String,
      default: '',
    },
    dir: {
      type: String,
      default: 'ltr',
    },

    headingLevel: {
      type: String,
      default: 'h3',
    },

    hasAlignment: {
      type: Boolean,
      default: false,
    },

    hasListNumbered: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editor: null,
      lastHtml: null,
      isHeading: false,
      isBold: false,
      isItalic: false,
      isUnderline: false,
      alignment: '',
      defaultAlignment: '',
    };
  },

  computed: {
    toStart() {
      return this.dir === 'rtl' ? 'right' : 'left';
    },

    toEnd() {
      return this.toStart === 'right' ? 'left' : 'right';
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.$nextTick(() => {
          if (this.lastHtml !== newVal) {
            this.editor.setHTML(newVal);
          }
        });
      },
    },
  },

  created() {
    this.alignment = this.dir === 'rtl' ? 'right' : 'left';
    this.defaultAlignment = this.alignment;
  },

  mounted() {
    this.initializeEditor();
    this.editor.addEventListener('input', this.updateOuter);
    this.editor.addEventListener('blur', this.emitBlurEvent);
    this.editor.addEventListener('pathChange', this.reevaluate);
    this.editor.addEventListener('willPaste', this.sanitizePastedHtml);
  },

  methods: {
    changeHeading() {
      this.isHeading = !this.isHeading;
      let tag;
      if (this.isHeading) {
        tag = this.headingLevel;
        this.editor.makeHeader(tag);
      } else {
        tag = 'p';
        this.editor.makeParagraph();
      }

      this.editor.setConfig({
        blockTag: tag,
      });

      this.editor.focus();
    },

    /**
     * changeStyle
     *
     * it applies 'bold', 'italic' and 'underline' font style
     *
     * @param {string} style one of the following styles: 'bold', 'italic' or 'underline'
     */
    changeStyle(style) {
      const normalizedStyle = style.toLowerCase();
      const capitalizedStyle =
        normalizedStyle.charAt(0).toUpperCase() + normalizedStyle.slice(1);
      const prop = `is${capitalizedStyle}`;

      if (!Reflect.has(this, prop)) {
        return false;
      }

      this[prop] = !this[prop];

      if (this[prop]) {
        this.editor[normalizedStyle]();
      } else {
        this.editor[`remove${capitalizedStyle}`]();
      }
    },

    formatListBulleted() {
      this.editor.makeUnorderedList();
    },

    formatListNumbered() {
      this.editor.makeOrderedList();
    },

    changeListLevel(dir) {
      if (
        (dir === 'left' && this.dir === 'rtl') ||
        (dir === 'right' && this.dir === 'ltr')
      ) {
        this.increaseListLevel();
      } else {
        this.decreaseListLevel();
      }
    },

    decreaseListLevel() {
      this.editor.decreaseListLevel();
    },

    increaseListLevel() {
      this.editor.increaseListLevel();
    },

    setTextAlignment(dir) {
      this.editor.setTextAlignment(dir);
      this.alignment = dir;
    },

    reevaluate() {
      if (this.editor.getPath() === '(selection)') {
        return false;
      }

      this.isHeading = this.editor.hasFormat(this.headingLevel);
      this.isBold = this.editor.hasFormat('strong');
      this.isItalic = this.editor.hasFormat('em');
      this.isUnderline = this.editor.hasFormat('u');

      let alignment = this.editor.getTextAlignment();

      if (!alignment) {
        alignment = this.defaultAlignment;
      }

      this.alignment = alignment;
    },

    updateOuter: function updateOuter() {
      this.lastHtml = this.editor.isEmpty() ? '' : this.editor.getHTML();
      this.$emit('input', this.lastHtml);
    },

    initializeEditor() {
      this.editor = new Squire(this.$refs.editor, {
        blockTag: 'p',
      });
    },

    sanitizePastedHtml(e) {
      const div = document.createElement('div');
      div.appendChild(e.fragment);

      const content = DOMPurify.sanitize(div.innerHTML, {
        ALLOWED_TAGS: [
          'strong',
          'b',
          'em',
          'i',
          'u',
          'h3',
          'h4',
          'br',
          'ul',
          'li',
          'p',
        ],
      });

      document.execCommand('insertHTML', false, content);
    },
    emitBlurEvent(e) {
      this.$emit('blur', e);
    },
  },
};
</script>

<style lang="scss">
@import '~@/stylesheets/element-variables';

$border-color: rgb(191, 201, 217);

.editor-menu {
  user-select: none;

  $menu: &;

  &__group {
    background: #fff;
    display: inline-flex;
    border: 1px solid $border-color;
    border-radius: 4px;
    vertical-align: bottom;
    overflow: hidden;

    margin-top: 0.25rem;
    margin-bottom: 0.25rem;

    @include breakpoint(medium) {
      margin-top: auto;
      margin-bottom: auto;

      & + & {
        margin-left: 5px;

        @include rtl-direct() {
          margin-left: auto;
          margin-right: 5px;
        }
      }
    }

    @include ltr-direct() {
      > #{$menu}__item {
        border-left: 1px $border-color solid;

        &:first-child {
          border-left: 0;
        }
      }
    }

    @include rtl-direct() {
      > #{$menu}__item {
        border-right: 1px $border-color solid;

        &:first-child {
          border-right: 0;
        }
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;

    padding: 0.5rem 0.35rem;

    &:hover {
      box-shadow: inset 0 -3px $wet-asphalt;
    }

    &.active {
      background: $wet-asphalt;
      color: #fff;
    }
  }

  &__icon {
    font-size: rem-calc(20);
  }
}

.editor-area {
  background: #fff;
  margin-top: 0.5rem;
  padding: $global-padding * 0.5 $global-padding;
  width: 100%;
  min-height: 200px;
  border: 1px solid $border-color;
  border-radius: 4px;
  cursor: text;
  color: rgb(31, 43, 61);
  overflow: hidden;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &#{&} {
    ul,
    ol {
      list-style-position: inside;
      margin-left: 0;
      margin-right: 0;
    }

    ul ul,
    ol ol {
      margin-left: 1.25rem;
      margin-right: auto;
    }

    &[dir='rtl'] ul ul,
    &[dir='rtl'] ol ol {
      margin-right: 1.25rem;
      margin-left: auto;
    }
  }

  &:focus {
    outline: none;
  }

  &:hover,
  &:focus {
    border-color: rgb(131, 143, 165);
  }

  .is-error & {
    border: 1px solid $--color-danger;
  }
}
</style>
